/**
 * ChapterHeroPanel — Upload Draft port of the PH360 "Hero Image" widget.
 *
 * Sits above the ChapterPreview and lets the user attach a per-chapter
 * illustration by uploading a file OR generating one with Gemini Nano
 * Banana. The image ships to the backend at
 *   POST   /api/books/:id/chapters/:sourceIndex/hero-image
 *   POST   /api/books/:id/chapters/:sourceIndex/hero-image/generate
 *   DELETE /api/books/:id/chapters/:sourceIndex/hero-image
 * where ``sourceIndex`` is the ``chapter_source_index`` field on the
 * chapter entry (stable across formatter_v2 role edits).
 *
 * The full book document is echoed back on every mutation so the parent
 * ``BookEditor`` refreshes and the Live Preview modal picks up the new
 * hero on next render.
 */
import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Sparkles, Trash2, Upload as UploadIcon, X, Wand2, AlertCircle } from "lucide-react";
import { api, formatApiError, isQuotaError } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ChapterHeroBatchModal } from "@/components/manual/ChapterHeroBatchModal";

const MAX_MB = 6;

export function ChapterHeroPanel({ book, chapter, onBookUpdated }) {
  const [busy, setBusy] = useState(null); // null | 'upload' | 'ai' | 'delete'
  const [prompt, setPrompt] = useState(chapter?.hero_image_prompt || "");
  const [mode, setMode] = useState(chapter?.hero_image_source === "ai" ? "ai" : "upload");
  const [batchOpen, setBatchOpen] = useState(false);
  const [quotaError, setQuotaError] = useState(null);
  const fileRef = useRef(null);

  // Count chapters still missing a hero — powers the batch action label.
  const missingCount = (book?.chapters || []).filter(
    (c) => c._kind === "chapter"
           && typeof c.chapter_source_index === "number"
           && !c.hero_image
  ).length;

  // A chapter without a chapter_source_index is a legacy (pre-iter-21)
  // upload — the user needs to re-upload the manuscript for hero images
  // to work. Show a clear message rather than a broken UI.
  const sourceIdx = chapter?.chapter_source_index;
  const heroSupported = typeof sourceIdx === "number";

  const onFileChosen = useCallback(async (e) => {
    const file = e.target?.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("File must be an image"); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`Image too large (max ${MAX_MB} MB)`); return; }
    setBusy("upload");
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(
        `/books/${book.id}/chapters/${sourceIdx}/hero-image`,
        fd, { headers: { "Content-Type": "multipart/form-data" } });
      onBookUpdated?.(data);
      toast.success("Hero image uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  }, [book.id, sourceIdx, onBookUpdated]);

  const onGenerate = useCallback(async () => {
    setBusy("ai");
    setQuotaError(null);
    toast.info("Generating hero image with Nano Banana — this can take 10–30 seconds.");
    try {
      const { data } = await api.post(
        `/books/${book.id}/chapters/${sourceIdx}/hero-image/generate`,
        { prompt: prompt.trim() || null });
      onBookUpdated?.(data);
      toast.success("Hero image generated");
    } catch (err) {
      if (isQuotaError(err)) {
        // Persistent banner so the user can see + act on the quota error
        // without a fleeting toast disappearing before they read it.
        setQuotaError(err.response.data.detail);
      } else if (err?.response?.status === 404) {
        // iter-28: after a DOCX re-upload the source_index space is
        // rebuilt from scratch. If the browser is holding stale chapter
        // state (e.g. a background tab that missed the last upload
        // response), the generate endpoint returns "Chapter with
        // source_index=X not found". Ask the parent to refetch the book
        // so the sidebar aligns with the server before the user retries.
        toast.error(
          "This chapter is out of sync with the server (its position changed after re-upload). "
          + "Refreshing chapter data — please try Generate again in a moment.",
          { duration: 6000 },
        );
        // Best-effort refresh: refetch the book document and hand it
        // back up so the sidebar chapters map to the current source_indices.
        try {
          const { data: fresh } = await api.get(`/books/${book.id}`);
          onBookUpdated?.(fresh);
        } catch (_ignored) { /* fall through — the toast still guides the user */ }
      } else {
        toast.error(formatApiError(err));
      }
    }
    finally { setBusy(null); }
  }, [book.id, sourceIdx, prompt, onBookUpdated]);

  const onDelete = useCallback(async () => {
    setBusy("delete");
    try {
      const { data } = await api.delete(`/books/${book.id}/chapters/${sourceIdx}/hero-image`);
      onBookUpdated?.(data);
      toast.success("Hero image removed");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  }, [book.id, sourceIdx, onBookUpdated]);

  if (!heroSupported) {
    // iter-28: legacy books (uploaded before ``chapter_source_index`` was
    // persisted, or whose chapter list is a stale in-memory snapshot)
    // land here. Instead of hiding the panel silently we surface a
    // clear "re-upload to enable hero images" nudge so the user isn't
    // left wondering why the AI/upload buttons never appear.
    return (
      <div className="mt-6 border border-amber-200 bg-amber-50 px-5 py-4 flex items-start gap-3"
           data-testid="chapter-hero-unsupported">
        <AlertCircle className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">Hero images not available for this chapter</p>
          <p className="text-xs mt-1 text-amber-800">
            Re-upload your manuscript from the Import panel — chapter positions
            need to be refreshed before hero-image generation can attach to them.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 border border-[color:var(--border)] bg-white p-4"
         data-testid="chapter-hero-panel"
         data-chapter-source-index={sourceIdx}>
      {quotaError && (
        <div className="mb-3 border border-amber-400 bg-amber-50 text-amber-900 px-3 py-2 text-sm flex items-start gap-2"
             role="alert"
             data-testid="chapter-hero-quota-banner">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>
          <div className="flex-1 min-w-0">
            <p className="font-medium">Nano Banana quota exceeded</p>
            <p className="text-xs mt-0.5">
              {quotaError.hint || "Add balance in Profile → Universal Key to keep generating."}
            </p>
            {quotaError.message && (
              <p className="text-[11px] mt-0.5 text-amber-800 truncate">{quotaError.message}</p>
            )}
            <div className="mt-1.5 flex items-center gap-3">
              <a href="/settings/emergent-key" className="text-xs underline"
                 data-testid="chapter-hero-quota-link">
                Open Profile → Universal Key
              </a>
              <button type="button" onClick={() => setQuotaError(null)}
                      className="text-xs underline text-amber-800"
                      data-testid="chapter-hero-quota-dismiss">
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-start gap-4">
        {/* Preview / placeholder ---------------------------------- */}
        <div className="w-32 h-24 shrink-0 border border-[color:var(--border)] bg-[color:var(--canvas)] flex items-center justify-center overflow-hidden"
             data-testid="chapter-hero-preview">
          {chapter.hero_image ? (
            <img src={chapter.hero_image}
                 alt=""
                 className="w-full h-full object-cover"
                 data-testid="chapter-hero-thumb"/>
          ) : (
            <ImagePlus className="w-6 h-6 text-[color:var(--text-muted)]" strokeWidth={1.25}/>
          )}
        </div>
        {/* Controls ----------------------------------------------- */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3 justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">§ Chapter hero image</p>
              <h4 className="text-sm font-medium mt-1">{chapter.title}</h4>
              {chapter.hero_image && (
                <p className="text-[11px] text-[color:var(--text-muted)] mt-0.5">
                  Source: <strong>{chapter.hero_image_source === "ai" ? "AI (Nano Banana)" : "Upload"}</strong>
                  {chapter.hero_image_prompt && ` · "${chapter.hero_image_prompt}"`}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setBatchOpen(true)}
              className="btn-ghost text-xs inline-flex items-center gap-1 shrink-0"
              title={missingCount > 0
                ? `Generate a hero for the ${missingCount} chapter(s) without one`
                : "Edit the shared batch prompt or regenerate later"}
              data-testid="chapter-hero-batch-trigger"
            >
              <Wand2 className="w-3.5 h-3.5"/>
              {missingCount > 0
                ? `Generate ${missingCount} missing`
                : "Edit batch prompt"}
            </button>
          </div>

          {/* Mode toggle */}
          <div className="mt-3 inline-flex border border-[color:var(--border)] text-xs">
            <button type="button"
                    className={`px-3 py-1 ${mode === "upload" ? "bg-[color:var(--canvas)] font-medium" : ""}`}
                    onClick={() => setMode("upload")}
                    data-testid="chapter-hero-mode-upload">
              Upload
            </button>
            <button type="button"
                    className={`px-3 py-1 border-l border-[color:var(--border)] ${mode === "ai" ? "bg-[color:var(--canvas)] font-medium" : ""}`}
                    onClick={() => setMode("ai")}
                    data-testid="chapter-hero-mode-ai">
              Generate with AI
            </button>
          </div>

          {/* Mode-specific controls */}
          {mode === "upload" ? (
            <div className="mt-3">
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFileChosen}
                     data-testid="chapter-hero-file-input"/>
              <button type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={busy === "upload"}
                      className="btn-primary text-sm inline-flex items-center gap-2"
                      data-testid="chapter-hero-upload-btn">
                <UploadIcon className="w-4 h-4"/>
                {busy === "upload" ? "Uploading…" : (chapter.hero_image ? "Replace image" : "Choose image")}
              </button>
              <p className="text-[11px] text-[color:var(--text-muted)] mt-1">
                JPG / PNG / WebP · max {MAX_MB} MB · wide landscape recommended.
              </p>
            </div>
          ) : (
            <div className="mt-3">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Optional prompt hint (leave blank to let the AI infer from the chapter title + opening)."
                rows={2}
                className="w-full border border-[color:var(--border)] px-2 py-1.5 text-sm focus:outline-none focus:border-[color:var(--primary)]"
                data-testid="chapter-hero-prompt-input"
              />
              <button type="button"
                      onClick={onGenerate}
                      disabled={busy === "ai"}
                      className="btn-primary text-sm inline-flex items-center gap-2 mt-2"
                      data-testid="chapter-hero-generate-btn">
                <Sparkles className="w-4 h-4"/>
                {busy === "ai" ? "Generating…" : (chapter.hero_image ? "Regenerate with AI" : "Generate with AI")}
              </button>
              <p className="text-[11px] text-[color:var(--text-muted)] mt-1">
                Uses Gemini Nano Banana · takes 10-30 seconds · no faces close-up, no watermarks.
              </p>
            </div>
          )}

          {/* Delete */}
          {chapter.hero_image && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="btn-ghost text-xs inline-flex items-center gap-1 mt-3 text-red-700 hover:text-red-800"
                        data-testid="chapter-hero-delete-trigger"
                        disabled={busy === "delete"}>
                  <Trash2 className="w-3.5 h-3.5"/> Remove hero image
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="chapter-hero-delete-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove hero image?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The hero image for <strong>{chapter.title}</strong> will be
                    removed from the preview and the exported PDF. This does not
                    affect any other chapters.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="chapter-hero-delete-cancel">Keep it</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete} data-testid="chapter-hero-delete-confirm">
                    Remove
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      {batchOpen && (
        <ChapterHeroBatchModal
          book={book}
          onClose={() => setBatchOpen(false)}
          onBookUpdated={onBookUpdated}
        />
      )}
    </div>
  );
}
